import React from 'react';
import axios from 'axios';

import { CONFIGURATION_FEATURES_NAMES } from '~/Types';

import { TooltipIcon } from '../components/core';
import {
  InvolvedBikerIcon,
  InvolvedBusinessIcon,
  InvolvedCyberIcon,
  InvolvedEntityIcon,
  InvolvedPersonIcon,
  InvolvedPetIcon,
  InvolvedPropertyIcon,
  InvolvedStructureIcon,
  InvolvedTravelerIcon,
  InvolvedVehicleIcon,
} from '../components/icons';
import {
  AlienIcon,
  AutoIcon,
  BikeIcon,
  BopIcon,
  CardProtectionIcon,
  CyberIcon,
  GeneralPropertyIcon,
  GLIcon,
  HomeIcon,
  PetIcon,
  PLIcon,
  TractorIcon,
  TravelIcon,
  WCIcon,
} from '../components/icons/lobs';

import { isFeatureEnabled, reportErrorInProductionOrThrow, reportGenericException } from '.';

export const isClaimWriteDisabled = (claim = {}, user, options = {}) => {
  return isClaimWriteDisabledIfClosed(claim.is_closed, user, options);
};

export const isClaimWriteDisabledIfClosed = (isClaimClosed, user, options = {}) => {
  const { allowOnClosedClaim = false, isAllowedForReadOnly = false } = options;

  if (isClaimClosed && !allowOnClosedClaim) {
    return true;
  }

  return !!(user.role.is_view_only && !isAllowedForReadOnly);
};

export function touchClaim(claimId) {
  // Not the user's own change: a frozen claim refuses it, and opening the claim must not greet them with a dialog.
  return axios.post(`/api/v1/claims/${claimId}/touch`, undefined, { sorSilent: true });
}

export function findIncidentParty(claim, incidentPartyId) {
  const partyType = claim.incident.party_types.find((partyType) => partyType.id === incidentPartyId).type;
  let partiesList;
  switch (partyType) {
    case 'vehicle_party':
      partiesList = claim.incident.vehicle_parties;
      break;
    case 'non_motorist_party':
      partiesList = claim.incident.non_motorist_parties;
      break;
    case 'involved_person_party':
      partiesList = claim.incident.involved_person_parties;
      break;
    case 'pet_party':
      partiesList = claim.incident.pet_parties;
      break;
    case 'generic_property_party':
      partiesList = claim.incident.other_properties_parties;
      break;
    default:
      throw Error(`Unknown incident party type ${partyType}`);
  }
  return partiesList.find((party) => party.id === incidentPartyId);
}

export function findInvolvedPerson(claim, involvedPerson) {
  if (['home_claim', 'gl_claim', 'general_claim'].includes(claim.type)) {
    return claim.incident.involved_persons.find((person) => person.id === involvedPerson.id);
  } else if (claim.type === 'auto_claim') {
    if (involvedPerson.type === 'involved_non_motorist') {
      const nonMotoristParty = claim.incident.non_motorist_parties.find(
        (non_motoristParty) => non_motoristParty.id === involvedPerson.incident_party_id
      );

      return nonMotoristParty.involved_non_motorist;
    } else {
      const vehicleParty = claim.incident.vehicle_parties.find(
        (vehicleParty) => vehicleParty.id === involvedPerson.incident_party_id
      );
      if (involvedPerson.type === 'involved_driver') {
        return vehicleParty.driver;
      } else if (involvedPerson.type === 'involved_passenger') {
        return vehicleParty.passengers.find((passenger) => passenger.id === involvedPerson.id);
      } else {
        throw Error('Should not happen');
      }
    }
  } else if (claim.type === 'wc_claim') {
    // Assume involved person can only be the involved employee. We can't return it like in gl/home claim because we'll lose the employee's fields (employee_id, job_title etc)
    return claim.employee_party.involved_person;
  } else if (claim.type === 'pet_claim') {
    reportErrorInProductionOrThrow('pet claim has no involved person'); // This flow shouldn't happen and it will probably crash anyway, but lets report...
    return undefined;
  } else {
    throw Error('Should not happen');
  }
}

export function findInvolvedProperty(claim, involvedProperty) {
  if (involvedProperty.type === 'generic_involved_property') {
    const genericParty = claim.incident.other_properties_parties.find(
      (otherPropertyParty) => otherPropertyParty.id === involvedProperty.incident_party_id
    );

    return genericParty.generic_property_involved;
  } else if (claim.type === 'auto_claim' && involvedProperty.type === 'involved_vehicle') {
    const vehicleParty = claim.incident.vehicle_parties.find(
      (vehicleParty) => vehicleParty.id === involvedProperty.incident_party_id
    );

    return vehicleParty.involved_vehicle;
  } else if (claim.type === 'pet_claim' && involvedProperty.type === 'involved_pet') {
    const petParty = claim.incident.pet_parties.find((petParty) => petParty.id === involvedProperty.incident_party_id);

    return petParty.involved_pet;
  }

  throw Error(`Should not happen, unknown involved property type: ${involvedProperty.type}`);
}

export function getFirstPartyVehicleFromClaim(claim) {
  return claim.incident.vehicle_parties.find((vehicleParty) => vehicleParty.id === claim.first_incident_party_id);
}

export function getFirstPartyFromClaim(claim) {
  return findIncidentParty(claim, claim.first_incident_party_id);
}

export function getThirdPartyVehiclesFromClaim(claim) {
  let thirdPartyVehicles = [];

  claim.incident.vehicle_parties.forEach((vehicleParty) => {
    if (vehicleParty.id !== claim.first_incident_party_id) {
      thirdPartyVehicles.push(vehicleParty);
    }
  });

  return thirdPartyVehicles;
}

const CLAIM_ICON_MAP = {
  home_claim: { title: 'Home Claim', Icon: HomeIcon },
  auto_claim: { title: 'Auto Claim', Icon: AutoIcon },
  gl_claim: { title: 'GL Claim', Icon: GLIcon },
  wc_claim: { title: 'WC Claim', Icon: WCIcon },
  pet_claim: { title: 'Pet Claim', Icon: PetIcon },
};

export const CLAIM_ICON_DICT = {
  auto_claim_icon: AutoIcon,
  bike_claim_icon: BikeIcon,
  cyber_claim_icon: CyberIcon,
  gl_claim_icon: GLIcon,
  home_claim_icon: HomeIcon,
  pet_claim_icon: PetIcon,
  travel_claim_icon: TravelIcon,
  wc_claim_icon: WCIcon,
  bop_claim_icon: BopIcon,
  alien_claim_icon: AlienIcon,
  professional_liability_claim_icon: PLIcon,
  card_protection_icon: CardProtectionIcon,
  general_property_icon: GeneralPropertyIcon,
  tractor_icon: TractorIcon,
};

export function getClaimIcon(claimType, iconProps) {
  try {
    const { Icon, title } = CLAIM_ICON_MAP[claimType];
    return (
      <TooltipIcon title={title}>
        <Icon {...iconProps} />
      </TooltipIcon>
    );
  } catch (error) {
    reportGenericException(
      new Error(`Can't find claim icon, claimType: ${claimType}, iconProps: ${JSON.stringify(iconProps)}`)
    );
    return <></>;
  }
}

export function getConfigurableLobClaimIcon(lobConfig, iconProps = {}) {
  const Icon = CLAIM_ICON_DICT[lobConfig.icon];
  try {
    if (!Icon) {
      reportGenericException(new Error(`Can't find icon for lob: ${lobConfig.icon}`));
      return <></>;
    }

    return (
      <TooltipIcon title={lobConfig.desc}>
        <Icon {...iconProps} />
      </TooltipIcon>
    );
  } catch (error) {
    reportGenericException(
      new Error(`Can't find claim icon for lob: ${JSON.stringify(lobConfig)}, iconProps: ${JSON.stringify(iconProps)}`)
    );
    return <></>;
  }
}

export const ENTITY_ICON_DICT = {
  person_icon: InvolvedPersonIcon,
  property_icon: InvolvedPropertyIcon,
  structure_icon: InvolvedStructureIcon,
  business_icon: InvolvedBusinessIcon,
  pet_icon: InvolvedPetIcon,
  biker_icon: InvolvedBikerIcon,
  entity_icon: InvolvedEntityIcon,
  traveler_icon: InvolvedTravelerIcon,
  cyber_icon: InvolvedCyberIcon,
  vehicle_icon: InvolvedVehicleIcon,
  card_protection_icon: CardProtectionIcon,
  general_property_icon: GeneralPropertyIcon,
  tractor_icon: TractorIcon,
};

export function getEntityIcon(entityIcon) {
  try {
    return ENTITY_ICON_DICT[entityIcon];
  } catch (error) {
    reportErrorInProductionOrThrow("Can't find entity icon");
    return InvolvedPersonIcon;
  }
}

export function getClaimExposuresWithGeneralFakeExposure(claim) {
  const FakeGeneralExpensesExposure = {
    id: 0,
    isGeneralExpenses: true,
    is_closed: claim.is_closed,
    is_pending_request_exists:
      claim.general_expenses.are_pending_payment_requests_exist ||
      claim.general_expenses.are_pending_reserve_change_requests_exist,
    expenses: claim.general_expenses,
  };

  return [...claim.exposures, FakeGeneralExpensesExposure];
}

export const getClaimTypeFromPolicy = (policy) => policy.type.replace('_policy', '_claim');

export const isReinsuranceManagementClaim = (claim, organization) =>
  isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.REINSURANCE_MANAGEMENT_TAB) &&
  claim.lob === 'reinsurance_claim';

export const REINSURANCE_TEXTS = {
  exposure: 'Layer',
  coverage: 'Layer',
  coverages: 'Layers',
  coinsurance: 'Share',
  deductible: 'Attachment Point',
  incident: 'Occurrence',
  policyholder: 'Insured',
  preferred_contact: 'Broker Name',
  limit_per_policy_per_entity: 'Limit Per Term',
  add_missing_coverage: 'Add Missing Layer',
  edit_coverage: 'Edit Layer',
  per_incident: 'per occurrence',
  per_policy: 'per term',
};

export const getReinsuranceTextIfNeeded = ({ key, isReinsuranceEnabled, defaultText }) => {
  return isReinsuranceEnabled ? REINSURANCE_TEXTS[key] || defaultText : defaultText;
};

/**
 * Extracts sub-organization ID from a claim, checking both direct property and policy fallback.
 * @param {Object|undefined} claim - The claim object (can be undefined)
 * @returns {number|null} The sub-organization ID or null if not found
 */
export const getSubOrganizationId = (claim) => {
  return claim?.sub_organization_id ?? claim?.policy?.sub_organization_id ?? null;
};
